import { takeLatest, put, call } from 'redux-saga/effects';
import {
  GET_EMAIL_SENT_HISTORY_REQUEST,
  GetEmailSentHistoryRequestAction,
  GET_DRAFT_EMAILS_REQUEST,
  GetDraftEmailsRequestAction,
  SEND_EMAIL_REQUEST,
  SendEmailRequestAction,
  SEND_ALL_EMAIL_REQUEST,
  SendAllEmailRequestAction,
  EXPORT_DRAFT_EMAILS_CSV_REQUEST,
  ExportDraftEmailsCSVRequestAction,
  EXPORT_SENT_EMAILS_CSV_REQUEST,
  ExportSentEmailsCSVRequestAction,
  SEND_TEST_EMAIL_REQUEST,
  SendTestEmailRequestAction,
  GET_EMAIL_VIEW_REQUEST,
  GetEmailViewRequestAction,
} from './emailSentHistory.types';
import {
  getEmailSentHistorySuccess,
  getEmailSentHistoryFailure,
  getDraftEmailsSuccess,
  getDraftEmailsFailure,
  sendEmailSuccess,
  sendEmailFailure,
  sendAllEmailSuccess,
  sendAllEmailFailure,
  exportDraftEmailsCSVSuccess,
  exportDraftEmailsCSVFailure,
  exportSentEmailsCSVSuccess,
  exportSentEmailsCSVFailure,
  sendTestEmailSuccess,
  sendTestEmailFailure,
  getEmailViewSuccess,
  getEmailViewFailure,
} from './emailSentHistory.action';
import {
  getEmailSentHistory,
  getDraftEmails,
  sendEmail,
  sendAllEmail,
  exportDraftEmailsCSV,
  exportSentEmailsCSV,
  sendTestEmail,
  getEmailView,
} from '../../services/emailSentHistoryService/emailSentHistoryService';

// Saga workers
function* getEmailSentHistoryWorker({
  payload: { campaignId, filters },
}: GetEmailSentHistoryRequestAction): Generator {
  try {
    const data: any = yield call(getEmailSentHistory, campaignId, filters);
    yield put(getEmailSentHistorySuccess(data));
  } catch (error: any) {
    yield put(
      getEmailSentHistoryFailure(
        error.response?.data || 'Failed to fetch email sent history'
      )
    );
  }
}

function* getDraftEmailsWorker({
  payload: { campaignId, filters },
}: GetDraftEmailsRequestAction): Generator {
  try {
    const data: any = yield call(getDraftEmails, campaignId, filters);
    yield put(getDraftEmailsSuccess(data));
  } catch (error: any) {
    yield put(
      getDraftEmailsFailure(
        error.response?.data || 'Failed to fetch draft emails'
      )
    );
  }
}

function* sendEmailWorker({
  payload: { emailId },
}: SendEmailRequestAction): Generator {
  try {
    const data: any = yield call(sendEmail, emailId);
    yield put(sendEmailSuccess(emailId, data.message));
  } catch (error: any) {
    yield put(
      sendEmailFailure(error.response?.data?.message || 'Failed to send email')
    );
  }
}

function* sendAllEmailWorker({
  payload: { campaignId },
}: SendAllEmailRequestAction): Generator {
  try {
    const data: any = yield call(sendAllEmail, campaignId);
    yield put(sendAllEmailSuccess(campaignId, data.message));
  } catch (error: any) {
    yield put(
      sendAllEmailFailure(
        error.response?.data?.message || 'Failed to send all emails'
      )
    );
  }
}

function* exportDraftEmailsCSVWorker({
  payload: { campaignId, startDate, endDate },
}: ExportDraftEmailsCSVRequestAction): Generator {
  try {
    const blob: any = yield call(
      exportDraftEmailsCSV,
      campaignId,
      startDate,
      endDate
    );
    yield put(exportDraftEmailsCSVSuccess(blob));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to export draft emails';
    yield put(exportDraftEmailsCSVFailure(errorMessage));
  }
}

function* exportSentEmailsCSVWorker({
  payload: { campaignId, startDate, endDate },
}: ExportSentEmailsCSVRequestAction): Generator {
  try {
    const blob: any = yield call(
      exportSentEmailsCSV,
      campaignId,
      startDate,
      endDate
    );
    yield put(exportSentEmailsCSVSuccess(blob));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to export sent emails';
    yield put(exportSentEmailsCSVFailure(errorMessage));
  }
}

function* sendTestEmailWorker({
  payload: { senderAccountId, emailTemplateId, contactId, emailAddress },
}: SendTestEmailRequestAction): Generator {
  try {
    const data: any = yield call(
      sendTestEmail,
      senderAccountId,
      emailTemplateId,
      contactId,
      emailAddress
    );
    yield put(sendTestEmailSuccess(data.message));
  } catch (error: any) {
    yield put(
      sendTestEmailFailure(
        error.response?.data?.message || 'Failed to send test email'
      )
    );
  }
}

function* getEmailViewWorker({
  payload: { emailId },
}: GetEmailViewRequestAction): Generator {
  try {
    const response: any = yield call(getEmailView, emailId);
    yield put(getEmailViewSuccess(response.message, response.data));
  } catch (error: any) {
    yield put(
      getEmailViewFailure(
        error.response?.data?.message || 'Failed to fetch email view'
      )
    );
  }
}

// Saga watchers
export function* emailSentHistorySaga() {
  yield takeLatest(GET_EMAIL_SENT_HISTORY_REQUEST, getEmailSentHistoryWorker);
  yield takeLatest(GET_DRAFT_EMAILS_REQUEST, getDraftEmailsWorker);
  yield takeLatest(SEND_EMAIL_REQUEST, sendEmailWorker);
  yield takeLatest(SEND_ALL_EMAIL_REQUEST, sendAllEmailWorker);
  yield takeLatest(EXPORT_DRAFT_EMAILS_CSV_REQUEST, exportDraftEmailsCSVWorker);
  yield takeLatest(EXPORT_SENT_EMAILS_CSV_REQUEST, exportSentEmailsCSVWorker);
  yield takeLatest(SEND_TEST_EMAIL_REQUEST, sendTestEmailWorker);
  yield takeLatest(GET_EMAIL_VIEW_REQUEST, getEmailViewWorker);
}
